"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { appsMainMenuHiddenClass, SHOW_APPS_MAIN_MENU } from "./navConfig";
import {
  isMainMenuPathActive,
  mainMenuItemClass,
  MAIN_MENU_TRIGGER_LAYOUT,
} from "./mainMenuStyles";
import { deriveLocaleFromPath, localizeHref, stripLocalePrefix } from "@/lib/i18n";
import { getMessages } from "@/lib/messages";
import { WatchOurStoryLink } from "./WatchOurStoryLink";

type MainMenuNavProps = {
  /** flyout = nested HIPAA submenu; flat = indented list (AppsNav). */
  appsMenu?: "flyout" | "flat";
};

export function MainMenuNav({ appsMenu = "flyout" }: MainMenuNavProps) {
  const pathname = usePathname() || "/";
  const ctx = deriveLocaleFromPath(pathname);
  const bare = stripLocalePrefix(pathname);
  const m = getMessages(ctx.locale);

  const appsActive = isMainMenuPathActive(bare, "/apps");
  const webSupportActive = isMainMenuPathActive(bare, "/web-support");
  const hostingActive = isMainMenuPathActive(bare, "/hosting");
  const helpActive = isMainMenuPathActive(bare, "/help");

  return (
    <nav
      className="main-menu-nav hidden items-center gap-6 text-sm md:flex dark:text-white/70"
      aria-label="Primary"
    >
      <div
        className={`relative group ${appsMainMenuHiddenClass()}`.trim()}
        aria-hidden={SHOW_APPS_MAIN_MENU ? undefined : true}
      >
        <button
          type="button"
          className={`${MAIN_MENU_TRIGGER_LAYOUT} ${mainMenuItemClass(appsActive)}`}
          aria-expanded="false"
          aria-haspopup="true"
          aria-label={m.nav.apps}
          aria-current={appsActive ? "page" : undefined}
        >
          {m.nav.apps}
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        <div className="absolute left-0 top-full z-50 overflow-visible pt-1">
          <div className="min-w-[200px] overflow-visible rounded-lg bg-white/70 py-1 shadow-lg ring-1 ring-white/30 backdrop-blur-xl opacity-0 invisible transition-[opacity,visibility] duration-150 group-hover:opacity-100 group-hover:visible group-focus-within:opacity-100 group-focus-within:visible dark:bg-white/10 dark:ring-white/20">
            {appsMenu === "flat" ? (
              <div>
                <Link
                  href="/apps/hipaa-compliant-forms"
                  className="block px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 dark:text-white/90 dark:hover:bg-white/15"
                >
                  {m.nav.hipaaForms}
                </Link>
                <Link href="/apps/hipaa-compliant-forms" className="block pl-7 pr-4 py-1 text-sm text-slate-500 hover:bg-slate-100 dark:text-white/60 dark:hover:bg-white/15">
                  {m.nav.overview}
                </Link>
                <Link href="/apps/hipaa-compliant-forms/register" className="block pl-7 pr-4 py-1 text-sm text-slate-500 hover:bg-slate-100 dark:text-white/60 dark:hover:bg-white/15">
                  {m.nav.getStarted}
                </Link>
                <Link href="/apps/hipaa-compliant-forms/pricing" className="block pl-7 pr-4 py-1 text-sm text-slate-500 hover:bg-slate-100 dark:text-white/60 dark:hover:bg-white/15">
                  {m.nav.pricing}
                </Link>
                <Link href="/apps/hipaa-compliant-forms/developers" className="block pl-7 pr-4 py-1 text-sm text-slate-500 hover:bg-slate-100 dark:text-white/60 dark:hover:bg-white/15">
                  {m.nav.developers}
                </Link>
                <Link href="/apps/hipaa-compliant-forms/developers/api" className="block pl-7 pr-4 py-1 text-sm text-slate-500 hover:bg-slate-100 dark:text-white/60 dark:hover:bg-white/15">
                  {m.nav.api}
                </Link>
                <Link href="/apps/hipaa-compliant-forms/integrations/wordpress" className="block pl-7 pr-4 py-1 text-sm text-slate-500 hover:bg-slate-100 dark:text-white/60 dark:hover:bg-white/15">
                  {m.nav.wordpress}
                </Link>
                <Link href="/apps/hipaa-compliant-forms/integrations/joomla" className="block pl-7 pr-4 py-1 text-sm text-slate-500 hover:bg-slate-100 dark:text-white/60 dark:hover:bg-white/15">
                  {m.nav.joomla}
                </Link>
                <Link href="/apps/hipaa-compliant-forms/faq" className="block pl-7 pr-4 py-1 text-sm text-slate-500 hover:bg-slate-100 dark:text-white/60 dark:hover:bg-white/15">
                  {m.nav.faq}
                </Link>
                <Link href="/apps/hipaa-compliant-forms/security" className="block pl-7 pr-4 py-1 text-sm text-slate-500 hover:bg-slate-100 dark:text-white/60 dark:hover:bg-white/15">
                  {m.nav.security}
                </Link>
                <div className="my-1 mx-4 border-t border-slate-200 dark:border-white/10" />
              </div>
            ) : (
              <div className="relative z-50 overflow-visible group/hipaa">
                <Link
                  href="/apps/hipaa-compliant-forms"
                  className="flex items-center justify-between px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 dark:text-white/90 dark:hover:bg-white/15"
                >
                  {m.nav.hipaaForms}
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
                <div className="absolute top-0 z-[60] left-[calc(100%-3rem)] min-w-[180px] rounded-lg bg-white/70 py-1 px-2 shadow-lg ring-1 ring-white/30 backdrop-blur-xl opacity-0 invisible transition-[opacity,visibility] duration-150 before:absolute before:right-full before:top-[-6px] before:h-[calc(100%+12px)] before:w-12 before:content-[''] group-hover/hipaa:opacity-100 group-hover/hipaa:visible group-focus-within/hipaa:opacity-100 group-focus-within/hipaa:visible dark:bg-white/10 dark:ring-white/20">
                  <Link href="/apps/hipaa-compliant-forms" className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 dark:text-white/90 dark:hover:bg-white/15">
                    {m.nav.overview}
                  </Link>
                  <Link href="/apps/hipaa-compliant-forms/register" className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 dark:text-white/90 dark:hover:bg-white/15">
                    {m.nav.getStarted}
                  </Link>
                  <Link href="/apps/hipaa-compliant-forms/pricing" className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 dark:text-white/90 dark:hover:bg-white/15">
                    {m.nav.pricing}
                  </Link>
                  <Link href="/apps/hipaa-compliant-forms/developers" className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 dark:text-white/90 dark:hover:bg-white/15">
                    {m.nav.developers}
                  </Link>
                  <Link href="/apps/hipaa-compliant-forms/developers/api" className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 dark:text-white/90 dark:hover:bg-white/15">
                    {m.nav.api}
                  </Link>
                  <Link href="/apps/hipaa-compliant-forms/integrations/wordpress" className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 dark:text-white/90 dark:hover:bg-white/15">
                    {m.nav.wordpress}
                  </Link>
                  <Link href="/apps/hipaa-compliant-forms/integrations/joomla" className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 dark:text-white/90 dark:hover:bg-white/15">
                    {m.nav.joomla}
                  </Link>
                  <Link href="/apps/hipaa-compliant-forms/faq" className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 dark:text-white/90 dark:hover:bg-white/15">
                    {m.nav.faq}
                  </Link>
                  <Link href="/apps/hipaa-compliant-forms/security" className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 dark:text-white/90 dark:hover:bg-white/15">
                    {m.nav.security}
                  </Link>
                </div>
              </div>
            )}
            <Link href="/apps/custom-apps" className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 dark:text-white/90 dark:hover:bg-white/15">
              {m.nav.customApps}
            </Link>
          </div>
        </div>
      </div>

      <Link
        href={localizeHref("/web-support", ctx)}
        className={mainMenuItemClass(webSupportActive)}
        aria-current={webSupportActive ? "page" : undefined}
      >
        {m.nav.webSupport}
      </Link>

      <Link
        href={localizeHref("/hosting", ctx)}
        className={mainMenuItemClass(hostingActive)}
        aria-current={hostingActive ? "page" : undefined}
      >
        {m.nav.hosting}
      </Link>

      <div className="relative z-50 group">
        <button
          type="button"
          className={`${MAIN_MENU_TRIGGER_LAYOUT} ${mainMenuItemClass(helpActive)}`}
          aria-expanded="false"
          aria-haspopup="true"
          aria-label={m.nav.help}
          aria-current={helpActive ? "page" : undefined}
        >
          {m.nav.help}
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        <div className="absolute left-0 top-full z-[60] overflow-visible before:absolute before:left-0 before:right-0 before:-top-3 before:h-3 before:content-['']">
          <div className="min-w-[160px] overflow-visible rounded-lg bg-white/70 py-1 shadow-lg ring-1 ring-white/30 backdrop-blur-xl opacity-0 invisible transition-[opacity,visibility] duration-150 group-hover:opacity-100 group-hover:visible group-focus-within:opacity-100 group-focus-within:visible dark:bg-white/10 dark:ring-white/20">
            <Link href={localizeHref("/help", ctx)} className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 dark:text-white/90 dark:hover:bg-white/15">
              {m.nav.helpCenter}
            </Link>
            <Link href={localizeHref("/help/faqs", ctx)} className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 dark:text-white/90 dark:hover:bg-white/15">
              {m.nav.faqs}
            </Link>
          </div>
        </div>
      </div>

      {bare === "/" ? <WatchOurStoryLink /> : null}

      <a
        href="https://panel.cloudaxe.io"
        className={mainMenuItemClass(false)}
        rel="noopener noreferrer"
        target="_blank"
      >
        {m.nav.clients}
      </a>
    </nav>
  );
}
